/**
 * Session Timeout Warning Component
 * Shows a modal warning before automatically logging out due to inactivity
 */

import { useState, useEffect, useCallback } from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useAuthStore } from '../stores/authStore';

export function SessionTimeoutWarning() {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(120);
  const { isAuthenticated } = useAuthStore();
  
  const handleWarning = useCallback((remainingMs: number) => {
    setShowWarning(true);
    setRemainingSeconds(Math.floor(remainingMs / 1000));
  }, []);
  
  const handleTimeout = useCallback(() => {
    setShowWarning(false);
  }, []);
  
  const { resetTimeout } = useSessionTimeout({
    onWarning: handleWarning,
    onTimeout: handleTimeout,
  });
  
  // Countdown timer
  useEffect(() => {
    if (!showWarning) return;
    
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [showWarning]);
  
  const handleStayLoggedIn = () => {
    setShowWarning(false);
    resetTimeout();
  };
  
  if (!isAuthenticated || !showWarning) return null;
  
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-dark-800 border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Warning Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-warning-500/20 flex items-center justify-center">
          <Clock className="h-8 w-8 text-warning-400 animate-pulse" />
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-bold text-white text-center mb-2">
          Session About to Expire
        </h2>
        
        {/* Message */}
        <p className="text-white/60 text-center mb-6">
          You will be automatically logged out due to inactivity.
        </p>
        
        {/* Countdown */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-dark-900 border border-white/10">
            <span className="text-3xl font-mono font-bold text-warning-400">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 btn-primary flex items-center justify-center gap-2 py-3"
          >
            <RefreshCw className="h-4 w-4" />
            Stay Logged In
          </button>
          <button
            onClick={() => {
              setShowWarning(false);
              useAuthStore.getState().logout();
            }}
            className="flex-1 btn-secondary flex items-center justify-center gap-2 py-3"
          >
            <LogOut className="h-4 w-4" />
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
}


